import { Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { LocationCode, LocationCodesService } from '@zayan-erp/home-building';
import { DynamicRouteHandlingService, InputFieldComponent } from '@zayan-erp/shared';
import { ToggleSwitchModule } from '@openng/optimus-ui/toggleswitch';

@Component({
  selector: 'lib-location-code-form',
  imports: [ReactiveFormsModule, InputFieldComponent, ToggleSwitchModule],
  standalone: true,
  templateUrl: './location-code-form.component.html'
})
export class LocationCodeFormComponent {
  #locationCodesService = inject(LocationCodesService);
  #dynamicRouteHandling = inject(DynamicRouteHandlingService);
  #destroyRef = inject(DestroyRef);
  #activatedRoute = inject(ActivatedRoute);
  #router = inject(Router);

  selectedLocationCodeId = signal<string | null>(this.#activatedRoute.snapshot.paramMap.get('id'));

  locationCodeForm = new FormGroup({
    locationCode: new FormControl<string | null>(null, Validators.required),
    locationName: new FormControl<string | null>(null, Validators.required),
    inActive: new FormControl<boolean>(false)
  });

  constructor() {
    const id = this.selectedLocationCodeId();
    if (id) {
      this.#locationCodesService.readLocationCodeById(id).subscribe({
        next: item => {
          this.locationCodeForm.patchValue({
            locationCode: item.locationCode ?? null,
            locationName: item.locationName ?? null,
            inActive: item.inActive ?? false
          });
        }
      });
    }
    this.#dynamicRouteHandling.submitButton$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => {
      this.onFormSubmit();
    });
  }

  onFormSubmit(): void {
    if (this.locationCodeForm.invalid) {
      this.locationCodeForm.markAllAsTouched();
      return;
    }

    if (this.selectedLocationCodeId()) {
      this.#locationCodesService
        .updateLocationCode(this.selectedLocationCodeId()!, this.getPayload() as Omit<LocationCode, 'id'>)
        .subscribe({
          next: () => this.onFormSuccessfulSubmit(),
          error: () => {}
        });
    } else {
      this.#locationCodesService.createLocationCode(this.getPayload()).subscribe({
        next: () => this.onFormSuccessfulSubmit(),
        error: () => {}
      });
    }
  }

  private getPayload(): Omit<LocationCode, 'id' | 'location' | 'discontinued'> {
    const v = this.locationCodeForm.value;
    return {
      locationCode: v.locationCode ?? '',
      locationName: v.locationName ?? '',
      inActive: v.inActive ?? true
    };
  }

  private onFormSuccessfulSubmit(): void {
    this.#dynamicRouteHandling.reload.next(!this.#dynamicRouteHandling.reload.value);
    this.#router.navigate(['/home-building/location-codes-management']);
    this.locationCodeForm.reset({
      locationCode: null,
      locationName: null,
      inActive: false
    });
  }
}
